import React, { useContext, useEffect, useState } from 'react'
import PageTitle from '../../Components/PageTitle'
import { ConfigContext } from '../../Context/ConfigContext'
import { GlobalLimitChanger, SubmitBtn } from '../../Components/InputElements'
import { ContentLoader, NoRecords, TableRows } from '../../Components/Shimmer'
import axios from 'axios'
import Swal from 'sweetalert2'

const Users = () => {
    const { primaryColor, apiHeaderJson, apiURL } = useContext(ConfigContext)
    const headers = apiHeaderJson

    const [userStatus, setUserStatus] = useState(0) // 0 = active, 1 = inactive
    const [loginId, setLoginId] = useState('')
    const [loginPassword, setLoginPassword] = useState('')
    const [data, setData] = useState([])
    const [userId, setUserId] = useState('')
    const [next, setNext] = useState(false)
    const [prev, setPrev] = useState(false)
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const [totalRecords, setTotalRecords] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [loading, setLoading] = useState(true)
    const [submitLoading, setSubmitLoading] = useState(false)
    const [keyword, setKeyword] = useState('')
    const [errors, setErrors] = useState({})
    const [isInfoLoading, setIsInfoLoading] = useState(false);

    const validateForm = () => {
        const errors = {}

        if (!loginId.trim()) {
            errors.loginId = "Login ID is required"
        }
        if (!loginPassword.trim()) {
            errors.loginPassword = "Password is required"
        }

        setErrors(errors)
        return Object.keys(errors).length === 0
    }

    const resetForm = () => {
        setUserStatus(0) // Default to active
        setLoginId("")
        setLoginPassword("")
        setErrors({})
        setUserId('')
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!validateForm()) return
        setSubmitLoading(true)
        try {
            const body = {
                user_status: userStatus,
                login_id: loginId,
                login_password: loginPassword
            }
            if (userId) {
                body.user_id = userId
            }
            const response = await axios.post(`${apiURL}Users/CreateUser`, body, { headers })
            const { message, success } = response.data

            if (success) {
                resetForm()
                getData()
                Swal.fire('Success!', message, 'success')
            }
        } catch (error) {
            console.log("error", error)
        } finally {
            setSubmitLoading(false)
        }
    }

    const getData = async () => {
        try {
            setLoading(true)
            const response = await axios.get(`${apiURL}Users/GetUsers`, {
                headers,
                params: {
                    keyword,
                    page,
                    limit
                }
            })
            const { success, data, page: currentPage, next, prev, total_pages, total_records } = response.data

            if (success) {
                setData(data)
                setPage(currentPage)
                setNext(next)
                setPrev(prev)
                setTotalPages(total_pages)
                setTotalRecords(total_records)
            }
        } catch (error) {
            console.log("error", error)
        } finally {
            setLoading(false)
        }
    }

    const getInfo = async (user_id) => {
        try {
            setIsInfoLoading(true);
            const response = await axios.get(`${apiURL}Users/GetUserInfo`, { headers, params: { user_id: user_id } })
            const { success, data } = response.data

            if (success) {
                setUserStatus(data[0].user_status)
                setLoginId(data[0].login_id)
                setLoginPassword(data[0].login_password)
                setUserId(data[0].user_id)
                setErrors({})
            }
        } catch (error) {
            console.log("error", error)
        } finally {
            setIsInfoLoading(false);
        }
    }

    const handleStatusChange = async (user_id, currentStatus) => {
        try {
            const newStatus = currentStatus === 0 ? 1 : 0;

            const body = {
                user_id: user_id,
                user_status: newStatus
            }

            const response = await axios.post(`${apiURL}Users/ActionUser`, body, { headers })
            const { message, success } = response.data

            if (success) {
                // Update local state immediately for better UX
                setData(prevData =>
                    prevData.map(user =>
                        user.user_id === user_id
                            ? { ...user, user_status: newStatus }
                            : user
                    )
                )
                Swal.fire('Success!', message, 'success')
            }
        } catch (error) {
            console.log("error", error)
            // Revert the checkbox on error
            getData(); // Reload data to sync with server
        }
    }

    const handleDelete = async (user_id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        })
        if (result.isConfirmed) {
            setLoading(true)
            try {
                const body = { user_id: user_id }
                const response = await axios.post(`${apiURL}Users/DeleteUser`, body, { headers })
                const { message, success } = response.data

                Swal.fire('Deleted!', message, success ? 'success' : 'error')

                if (success) {
                    getData()
                }
            } catch (error) {
                console.error(error)
            } finally {
                setLoading(false)
            }
        }
    }

    const handlePrev = () => {
        if (prev) {
            setPage(prev => prev - 1)
        }
    }

    const handleNext = () => {
        if (next) {
            setPage(prev => prev + 1)
        }
    }

    const handleChange = (e) => {
        setPage(parseInt(e.target.value, 10))
    }

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            getData()
        }, 500)

        return () => clearTimeout(delayDebounce)
    }, [keyword])

    useEffect(() => {
        getData()
    }, [limit, page])

    return (
        <>
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <PageTitle title="Manage Users" primary="Accounts" />
                        {isInfoLoading && <ContentLoader />}
                        <div className="row">
                            <div className="col-md-4">
                                <div className="card">
                                    <div className="card-header" style={{ backgroundColor: primaryColor }}>
                                        <h4 className="card-title mb-0 text-white">New User</h4>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-12 mb-3">
                                                <label htmlFor="loginId">Login ID:</label>
                                                <input
                                                    type="text"
                                                    id="loginId"
                                                    name="loginId"
                                                    value={loginId}
                                                    onChange={(e) => {
                                                        setLoginId(e.target.value);
                                                        if (errors.loginId) {
                                                            setErrors({ ...errors, loginId: "" })
                                                        }
                                                    }}
                                                    className={`form-control ${errors.loginId ? 'is-invalid' : ''}`}
                                                    placeholder="Enter Login ID"
                                                />
                                                {errors.loginId && <div className="invalid-feedback">{errors.loginId}</div>}
                                            </div>
                                            <div className="col-md-12 mb-3">
                                                <label htmlFor="loginPassword">Password:</label>
                                                <input
                                                    type="text"
                                                    id="loginPassword"
                                                    name="loginPassword"
                                                    value={loginPassword}
                                                    onChange={(e) => {
                                                        setLoginPassword(e.target.value);
                                                        if (errors.loginPassword) {
                                                            setErrors({ ...errors, loginPassword: "" })
                                                        }
                                                    }}
                                                    className={`form-control ${errors.loginPassword ? 'is-invalid' : ''}`}
                                                    placeholder="Enter Password"
                                                />
                                                {errors.loginPassword && <div className="invalid-feedback">{errors.loginPassword}</div>}
                                            </div>
                                            <div className="col-md-12 mb-3">
                                                <label>Status:</label>
                                                <div className="d-flex gap-3">
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="userStatus"
                                                            id="active"
                                                            value={0}
                                                            checked={userStatus === 0}
                                                            onChange={() => setUserStatus(0)}
                                                        />
                                                        <label className="form-check-label" htmlFor="active">
                                                            Active
                                                        </label>
                                                    </div>
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="userStatus"
                                                            id="inactive"
                                                            value={1}
                                                            checked={userStatus === 1}
                                                            onChange={() => setUserStatus(1)}
                                                        />
                                                        <label className="form-check-label" htmlFor="inactive">
                                                            Inactive
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-12 mb-3">
                                                <div className='col-md-12 d-flex align-items-end justify-content-end gap-3 mt-3'>
                                                    {userId ? (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="btn btn-light btn-label right me-auto"
                                                                onClick={resetForm}
                                                            >
                                                                Reset
                                                                <i className="ri-arrow-right-line label-icon align-middle fs-16" />
                                                            </button>
                                                            <SubmitBtn
                                                                icon="ri-save-line"
                                                                text="Update"
                                                                onClick={handleSubmit}
                                                                type="primary"
                                                            />
                                                        </>
                                                    ) : (
                                                        <SubmitBtn
                                                            disabled={submitLoading}
                                                            text={submitLoading ? 'Saving..' : 'Save'}
                                                            type="primary"
                                                            onClick={handleSubmit}
                                                            icon="ri-save-line"
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-8">
                                <div className="card">
                                    <div className="card-header" style={{ backgroundColor: primaryColor }}>
                                        <div className="row d-flex align-items-center">
                                            <div className="col-md-7">
                                                <h4 className="card-title mb-0 text-white">Users List</h4>
                                            </div>
                                            <div className="col-md-5">
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control pe-4"
                                                        placeholder="Search"
                                                        name='keyword'
                                                        value={keyword}
                                                        onChange={(e) => setKeyword(e.target.value)}
                                                    />
                                                    <span className="position-absolute end-0 top-50 translate-middle-y me-3">
                                                        <i className="ri-search-line"></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="table-responsive table-card">
                                            <table className="table table-bordered table-striped table-nowrap table-hover mb-0">
                                                {loading ? (
                                                    <TableRows rows="10" colspan="5" />
                                                ) : (
                                                    <>
                                                        <thead className="table-light text-center">
                                                            <tr>
                                                                <th>Login ID</th>
                                                                <th>Password</th>
                                                                <th>Status</th>
                                                                <th>Active/Inactive</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {data.length > 0 ? (
                                                                data.map((row) => (
                                                                    <tr key={row.user_id} className="text-center">
                                                                        <td>{row.login_id}</td>
                                                                        <td>{row.login_password}</td>
                                                                        <td>
                                                                            <span className={`badge badge bg-${row.user_status === 0 ? 'success' : 'danger'}`}>
                                                                                {row.user_status === 0 ? 'Active' : 'Inactive'}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            <div className="form-check form-switch d-flex justify-content-center">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="checkbox"
                                                                                    role="switch"
                                                                                    checked={row.user_status === 0}
                                                                                    onChange={() => handleStatusChange(row.user_id, row.user_status)}
                                                                                    style={{ width: '3em', height: '1.5em' }}
                                                                                />
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <button
                                                                                className="btn btn-sm btn-soft-primary me-2"
                                                                                onClick={() => getInfo(row.user_id)}
                                                                            >
                                                                                <i className='ri-pencil-line'></i>
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-sm btn-soft-danger"
                                                                                onClick={() => handleDelete(row.user_id)}
                                                                            >
                                                                                <i className='ri-delete-bin-line'></i>
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan={5}>
                                                                        <NoRecords />
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                        <tfoot className='table-light'>
                                                            <tr>
                                                                <th colSpan={5}>
                                                                    <div className="d-flex justify-content-between">
                                                                        <button disabled={!prev && !loading} type="button" onClick={handlePrev} className={`btn btn-warning btn-label waves-effect waves-light`}>
                                                                            <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2" /> Previous
                                                                        </button>
                                                                        <div className='col-md-4' style={{ display: 'flex', alignItems: 'center' }}>
                                                                            <small>Total Records: {totalRecords} | Total Pages: {totalPages} | Current Page: {page}</small>
                                                                        </div>
                                                                        <div className='col-md-2'>
                                                                            <select className="form-select" value={page} onChange={handleChange}>
                                                                                {Array.from({ length: totalPages }, (_, i) => (
                                                                                    <option key={i + 1} value={i + 1}>
                                                                                        {i + 1}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        </div>
                                                                        <div className='col-md-2'>
                                                                            <GlobalLimitChanger
                                                                                placeholder="Set limit:"
                                                                                name="globalLimit"
                                                                                value={limit}
                                                                                onChange={setLimit}
                                                                                showAllValue={totalRecords}
                                                                            />
                                                                        </div>
                                                                        <button disabled={!next && !loading} type="button" onClick={handleNext} className={`btn btn-primary btn-label waves-effect right waves-light`}>
                                                                            <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2" /> Next
                                                                        </button>
                                                                    </div>
                                                                </th>
                                                            </tr>
                                                        </tfoot>
                                                    </>
                                                )}
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Users